import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import { Error, MoreVert, Replay } from "@mui/icons-material"
import {
  CircularProgress,
  ListItemIcon,
  MenuItem,
  Stack,
  styled,
  Typography,
} from "@mui/material"
import dayjs from "dayjs"
import { FC, memo, PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { Order } from "src/db/Order"
import {
  MenuWrapper,
  TooltipWrapper,
} from "src/features/shared-module/components"
import {
  appConstants,
  defaultEmployeeOrders,
} from "src/features/shared-module/constants"
import { Attributes } from "src/features/shared-module/models"
import { useAppSelector } from "../hooks"
import { useRouteDetail } from "../hooks/useRouteDetail"
import { setPartialVisibilityInMap } from "../slices/dashboard.slice"
import { CommonSortableOrderList } from "./CommonSortableOrderList"

export const EmployeeOrderList: FC<{
  haulerEmployeeId: number
}> = memo(({ haulerEmployeeId }) => {
  const employeeAllOrderDetail = useAppSelector((state) => {
    return (
      state.dashboard.employeeOrders?.[haulerEmployeeId] ||
      defaultEmployeeOrders
    )
  })
  const { grouped: groupedOrdersMap, individual: orderListWithoutRoute } =
    employeeAllOrderDetail

  return (
    <Stack
      {...uiSurface("UI-DSP-032")}
      data-testid={`employee-orders-${haulerEmployeeId}`}
      spacing={1}
      sx={(theme) => ({
        background: theme.palette.grey[100],
        padding: 1,
        borderRadius: 3,
      })}
    >
      {Object.entries(groupedOrdersMap).map(
        ([routeSummaryId, items]: [string, Order[]]) => {
          return (
            <Stack
              key={`route-${routeSummaryId}`}
              sx={{
                "& > .MuiBox-root:nth-of-type(2)": {
                  ".MuiDivider-root::before": {
                    visibility: "hidden",
                  },
                },
                "& > .MuiBox-root:last-of-type": {
                  ".MuiDivider-root::after": {
                    visibility: "hidden",
                  },
                },
              }}
            >
              <RouteSummaryHeader
                haulerEmployeeId={haulerEmployeeId}
                routeSummaryId={Number(routeSummaryId)}
              />
              <CommonSortableOrderList
                contextId={`${haulerEmployeeId}-${routeSummaryId}`}
                items={items as any[]}
              />
            </Stack>
          )
        },
      )}
      <Stack spacing={1}>
        <IndividualOrderHeader haulerEmployeeId={haulerEmployeeId} />
        <CommonSortableOrderList
          contextId={`${haulerEmployeeId}`}
          items={orderListWithoutRoute as any[]}
        />
      </Stack>
    </Stack>
  )
})

/* order list header */
const StyledOrderHeader = styled(Stack)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
}))

const RouteSummaryHeader: FC<
  PropsWithChildren<{ haulerEmployeeId: number; routeSummaryId: number }>
> = ({ haulerEmployeeId, routeSummaryId }) => {
  const { t } = useTranslation()
  const routeSummary = useAppSelector(
    (state) => state.dashboard.orderRouteSummaryMap?.[routeSummaryId],
  )
  const routeDetail = useRouteDetail(routeSummaryId)

  const dispatch = useDispatch()
  const onCalculateETA = () => {}
  const onOptimizeRoute = () => {}

  const onChangeMapDetailVisibility = () => {
    dispatch(
      setPartialVisibilityInMap({
        haulerEmployeeId: haulerEmployeeId,
        routeSummaryId: routeSummaryId,
      }),
    )
  }

  if (routeSummary?.error) {
    return (
      <StyledOrderHeader>
        <Stack direction={"row"} spacing={1} marginLeft={"auto"}>
          <Typography color="error" variant="h6">
            {t("genericErrorMessage")}...
          </Typography>
          <Error color="error" fontSize="small" />
        </Stack>
      </StyledOrderHeader>
    )
  }
  if (routeSummary?.isLoading) {
    return (
      <StyledOrderHeader>
        <Stack direction={"row"} spacing={1} marginLeft={"auto"}>
          <Typography variant="h6">{t("routeDetailLoading")}...</Typography>
          <CircularProgress size={"1.4rem"} />
        </Stack>
      </StyledOrderHeader>
    )
  }
  return (
    <StyledOrderHeader>
      {routeDetail && (
        <TooltipWrapper
          title={<RouteSummaryHeadingLabel routeDetail={routeDetail} />}
        >
          <Typography variant="subtitle2" noWrap={true}>
            <RouteSummaryHeadingLabel routeDetail={routeDetail} />
          </Typography>
        </TooltipWrapper>
      )}
      <Stack marginLeft={"auto"}>
        <MenuWrapper
          menuIcon={<MoreVert />}
          menuProps={{ slotProps: { paper: uiPaper("UI-DSP-033") } }}
        >
          <MenuItem
            data-testid={`route-calculate-eta-${routeSummaryId}`}
            onClick={onCalculateETA}
          >
            <ListItemIcon>
              <Replay fontSize="small" />
            </ListItemIcon>
            {t("calculateETA")}
          </MenuItem>
          <MenuItem
            data-testid={`route-optimize-${routeSummaryId}`}
            onClick={onOptimizeRoute}
          >
            <ListItemIcon>
              <Replay fontSize="small" />
            </ListItemIcon>
            {t("optimizeRoute")}
          </MenuItem>
          <MenuItem
            data-testid={`route-toggle-visibility-${routeSummaryId}`}
            onClick={onChangeMapDetailVisibility}
          >
            <ListItemIcon>
              <Replay fontSize="small" />
            </ListItemIcon>
            {t("showHideRouteDetail")}
          </MenuItem>
        </MenuWrapper>
      </Stack>
    </StyledOrderHeader>
  )
}

const IndividualOrderHeader: FC<
  PropsWithChildren<{ haulerEmployeeId: number }>
> = ({ haulerEmployeeId }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const onChangeMapDetailVisibility = () => {
    dispatch(
      setPartialVisibilityInMap({
        haulerEmployeeId: haulerEmployeeId,
        routeSummaryId: null,
      }),
    )
  }

  return (
    <StyledOrderHeader>
      <Typography
        data-testid={`employee-no-route-${haulerEmployeeId}`}
        variant="body1"
      >
        {t("noRoute")}
      </Typography>
      <MenuWrapper
        menuIcon={<MoreVert />}
        menuProps={{ slotProps: { paper: uiPaper("UI-DSP-034") } }}
      >
        <MenuItem
          data-testid={`employee-toggle-visibility-${haulerEmployeeId}`}
          onClick={onChangeMapDetailVisibility}
        >
          <ListItemIcon>
            <Replay fontSize="small" />
          </ListItemIcon>
          {t("showHideRouteDetail")}
        </MenuItem>
      </MenuWrapper>
    </StyledOrderHeader>
  )
}

const RouteSummaryHeadingLabel: FC<{ routeDetail: Attributes }> = ({
  routeDetail,
}) => {
  const { t } = useTranslation()
  const endTime = dayjs(routeDetail.EndTime).format(appConstants.hourTimeFormat)

  return t("routeSummaryHeadLabel", {
    routeName: routeDetail.Name ?? "",
    endTime: endTime,
    totalDistance: routeDetail.TotalKilometers?.toFixed(2) ?? "—",
  })
}
